/**
 * Variant traffic weights, in basis points out of a 10000 total.
 *
 * The invariant this file exists to protect: a concrete distribution always sums
 * to exactly TOTAL_WEIGHT. Historically the dashboard, the API and the runtime
 * each did their own weight arithmetic and disagreed — a stored `null` meant
 * "share the remainder" to the dashboard and "zero traffic" to the runtime, and
 * single-variant writes could leave the stored sum above or below the total,
 * silently starving variants or dropping visitors.
 *
 * Shared rather than colocated with the weights UI: the modal previews a
 * redistribution, the API validates it, and the start handler resolves floating
 * weights into concrete ones. Those three must reach identical numbers, so they
 * call identical code.
 */

export const TOTAL_WEIGHT = 10000;

export type WeightEntry = { id: number; weight: number };

type StoredVariant = { id: number; weight: number | null };

/**
 * `total` split into `count` near-equal integer shares, the flooring residue
 * going one unit at a time to the earliest shares. Every even split in this
 * file goes through here so the residue-placement rule exists exactly once.
 */
function evenShares(total: number, count: number): number[] {
  const base = Math.floor(total / count);
  const extra = total % count;
  return Array.from({ length: count }, (_, index) => base + (index < extra ? 1 : 0));
}

/**
 * Resolve a stored distribution into concrete basis points.
 *
 * Explicit weights are kept as-is. Floating variants (weight === null) share
 * whatever remains equally; the flooring residue is spread one basis point at a
 * time across the first floating variants, so the result sums to exactly
 * TOTAL_WEIGHT. Callers pass variants in creation order, which puts Original
 * first and gives it the residue — matching what the dashboard has always shown.
 *
 * Returns null when the distribution cannot be resolved: explicit weights alone
 * exceed the total, an explicit weight is invalid, or there is no floating
 * variant left to absorb a shortfall.
 */
export function resolveWeights(variants: readonly StoredVariant[]): WeightEntry[] | null {
  if (variants.length === 0) return [];

  const explicit = variants.filter((v) => v.weight !== null);
  const floating = variants.filter((v) => v.weight === null);

  if (explicit.some((v) => !Number.isInteger(v.weight) || v.weight! < 0 || v.weight! > TOTAL_WEIGHT)) return null;

  const explicitSum = explicit.reduce((sum, v) => sum + v.weight!, 0);
  const remaining = TOTAL_WEIGHT - explicitSum;
  if (remaining < 0) return null;

  if (floating.length === 0) {
    if (remaining !== 0) return null;
    return variants.map((v) => ({ id: v.id, weight: v.weight! }));
  }

  const shares = evenShares(remaining, floating.length);
  const floatingWeights = new Map(floating.map((v, index) => [v.id, shares[index]!] as const));

  return variants.map((v) => ({ id: v.id, weight: v.weight ?? floatingWeights.get(v.id)! }));
}

/**
 * An even split across the given variants, residue going to the first ones —
 * three variants become 3334/3333/3333 with Original (first by creation) on top.
 */
export function evenDistribution(ids: readonly number[]): WeightEntry[] {
  if (ids.length === 0) return [];
  const shares = evenShares(TOTAL_WEIGHT, ids.length);
  return ids.map((id, index) => ({ id, weight: shares[index]! }));
}

/**
 * Scale entries proportionally so they sum to exactly `target`, using the
 * largest-remainder method for the rounding residue. All-zero entries split the
 * target evenly instead.
 */
function scaleToTarget(entries: readonly WeightEntry[], target: number): Map<number, number> {
  const scaledById = new Map<number, number>();
  if (entries.length === 0) return scaledById;

  const total = entries.reduce((sum, entry) => sum + entry.weight, 0);
  if (total <= 0) {
    const shares = evenShares(target, entries.length);
    entries.forEach((entry, index) => scaledById.set(entry.id, shares[index]!));
    return scaledById;
  }

  const exact = entries.map((entry) => ({ id: entry.id, value: (entry.weight * target) / total }));
  let allocated = 0;
  for (const { id, value } of exact) {
    const floored = Math.floor(value);
    scaledById.set(id, floored);
    allocated += floored;
  }
  const byRemainder = exact
    .map(({ id, value }) => ({ id, frac: value - Math.floor(value) }))
    .sort((a, b) => b.frac - a.frac);
  for (let i = 0; i < target - allocated; i++) {
    const id = byRemainder[i]!.id;
    scaledById.set(id, scaledById.get(id)! + 1);
  }
  return scaledById;
}

type RedistributeParams = {
  entries: readonly WeightEntry[];
  changedId: number;
  newWeight: number;
};

/**
 * Set one variant's weight and scale the others proportionally into the space
 * that remains, preserving their relative proportions. When the others are all
 * at zero they split the remainder evenly instead. The result always sums to
 * exactly TOTAL_WEIGHT.
 */
export function redistributeWeights({ entries, changedId, newWeight }: RedistributeParams): WeightEntry[] {
  const changed = entries.find((entry) => entry.id === changedId);
  if (!changed) return entries.map((entry) => ({ ...entry }));

  const clamped = Math.min(Math.max(Math.round(newWeight), 0), TOTAL_WEIGHT);
  const others = entries.filter((entry) => entry.id !== changedId);
  if (others.length === 0) return [{ id: changedId, weight: TOTAL_WEIGHT }];

  const scaledById = scaleToTarget(others, TOTAL_WEIGHT - clamped);

  return entries.map((entry) => ({
    id: entry.id,
    weight: entry.id === changedId ? clamped : scaledById.get(entry.id)!,
  }));
}

/**
 * Why a submitted distribution is unacceptable, or null if it is fine. Meant for
 * the API boundary: the message is safe to return to the dashboard as-is.
 */
export function distributionError(entries: readonly WeightEntry[], expectedIds: readonly number[]): string | null {
  const submitted = new Set(entries.map((entry) => entry.id));
  if (submitted.size !== entries.length) return 'Each variant may appear only once';
  if (expectedIds.length !== entries.length || expectedIds.some((id) => !submitted.has(id))) {
    return 'Weights must cover every variant of the experiment';
  }
  for (const { weight } of entries) {
    if (!Number.isInteger(weight) || weight < 0 || weight > TOTAL_WEIGHT) {
      return 'Each weight must be a whole number of basis points between 0 and 10000';
    }
  }
  const sum = entries.reduce((total, entry) => total + entry.weight, 0);
  if (sum !== TOTAL_WEIGHT) return `Weights must sum to 100% (got ${(sum / 100).toFixed(2)}%)`;
  return null;
}
